import { useEffect, useRef, useState } from 'react';
import crc16modbus from 'crc/crc16modbus';

import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Battery5BarIcon from '@mui/icons-material/Battery5Bar';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import ElectricBikeIcon from '@mui/icons-material/ElectricBike';
import SpeedIcon from '@mui/icons-material/Speed';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import Connect from './components/Connect';
import CurrentSpeed from './components/CurrentSpeed';
import BigNumber from './components/BigNumber';
import Settings from './components/Settings';
import DebugDialog from './components/DebugDialog';

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://jasperw.de/">
        Jasper Witkowski
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const properties = [
  {
    name: "property0",
    number: 0,
    bytes: 1
  },
  {
    name: "modelNumber",
    number: 1,
    bytes: 1,
    type: "utf8"
  },
  {
    name: "hwVersion",
    number: 2,
    bytes: 1,
    type: "utf8"
  },
  {
    name: "fwVersion",
    number: 3,
    bytes: 1,
    type: "utf8"
  },
  {
    name: "serialNumber",
    number: 7,
    bytes: 1,
    type: "utf8"
  },
  {
    name: "currentSpeed",
    number: 17,
    bytes: 2,
    type: "decimal"
  },
  {
    name: "maxSpeed",
    number: 18,
    bytes: 2,
    type: "decimal"
  },
  {
    name: "avgSpeed",
    number: 19,
    bytes: 2,
    type: "decimal"
  },
  {
    name: "trip",
    number: 20,
    bytes: 4,
    type: "decimal"
  },
  {
    name: "tripTime",
    number: 21,
    bytes: 2,
    type: "decimal"
  },
  {
    name: "range",
    number: 22,
    bytes: 4,
    type: "decimal"
  },
  {
    name: "mileage",
    number: 23,
    bytes: 4,
    type: "decimal"
  },
  {
    name: "voltage",
    number: 24,
    bytes: 2,
    type: "decimal"
  },
  {
    name: "currentPercent", // 0 - 100 (+-10) -> Current based on BluetoothModel.class in App
    number: 25,
    bytes: 2,
    type: "decimal"
  },
  {
    name: "battery",
    number: 26,
    bytes: 1,
    type: "decimal"
  },
  {
    name: "property27", // error code? -> 1 means everything is fine?
    number: 27,
    bytes: 1
  },
  {
    name: "light",
    number: 33,
    bytes: 1,
    type: "bool"
  },
  {
    name: "supportLevel",
    number: 34,
    bytes: 1,
    type: "decimal"
  },
  {
    name: "property36", // boost state?
    number: 36,
    bytes: 1
  },
  {
    name: "speedLimit",
    number: 37,
    bytes: 2,
    type: "bool-and-decimal"
  },
  {
    name: "wheelSize",
    number: 38,
    bytes: 2,
    type: "hex"
  },
  {
    name: "screenTimeout",
    number: 40,
    bytes: 2,
    type: "decimal"
  },
  {
    name: "unit",
    number: 42,
    bytes: 1,
    type: "bool"
  },
  {
    name: "pin",
    number: 43,
    bytes: 3,
    type: "bool-and-hex"
  },
  {
    name: "walkingAssist",
    number: 45,
    bytes: 1,
    type: "bool"
  },
  {
    name: "property252",
    number: 252,
    bytes: 1
  },
  {
    name: "property253",
    number: 253,
    bytes: 2
  }
]

const defaultPropertyNumbers = [ 17, 18, 19, 20, 21, 22, 23, 26, 33, 34, 37, 42 ]

/* source: https://stackoverflow.com/questions/40031688/how-can-i-convert-an-arraybuffer-to-a-hexadecimal-string-hex */
function buf2hex(buffer) { // buffer is an ArrayBuffer
  return [...new Uint8Array(buffer)]
      .map(x => x.toString(16).padStart(2, '0'))
      .join('');
}

export default function App() {
  const [device, setDevice] = useState("")
  const [service, setService] = useState("")
  const [polling, setPolling] = useState(false)
  const [propertyNumbers, setPropertyNumbers] = useState(defaultPropertyNumbers)
  const [rawData, setRawData] = useState([])
  const [data, setData] = useState([])
  const [btBusy, setBtBusy] = useState(false)

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const defaultTheme = createTheme({
    breakpoints: {
      values: {
        xxs: 0,
        xs: 430,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    palette: {
      secondary: {
        main: '#5a668d',
        light: '#949dc2',
        dark: '#252d47'
      },
      mode: prefersDarkMode ? "dark" : "light"
    }
  });

  const interval = useRef()

  const multiplicator = data?.unit ? 0.6214 : 1

  const btSend = (opcode, bytes, value) => {
    setRawData([])
    setBtBusy(true)

    let arr = [ 90, 0 ]
    arr.push(opcode)
    arr.push(bytes)
    
    arr = (bytes > 0 && value && value.length) ? arr.concat(value) : arr
  
    let checksum = crc16modbus(arr).toString(16)
    console.log("my checksum is", checksum)

    if (checksum.length < 4) {
      const difference = 4 - checksum.length
      
      for (let i=0; i < difference; i++) {
        checksum = "0" + checksum
      }

      console.log("my checksum is now", checksum)
    }

    arr.push("0x" + checksum[0] + checksum[1])
    arr.push("0x" + checksum[2] + checksum[3])
    // arr.push(0)
    // let checksum = 0
    // arr.forEach(item => checksum += parseInt(item))
    // console.log(checksum)
    // arr.push(checksum % 256)
    arr.push(165)
    
    const writeValue = new Uint8Array(arr)
    // console.log(writeValue)

    btExecute(writeValue)
    // e. g. btSend([90,1,79,0])
    // gives back: [91,1,79,9,0,22,5,1,7,0,30,0,2,0,247,165]
    //                        =bool unit
    //                          =hex 1605 -> pin
    //                                 = 700C -> wheel size
    //                                     = 30min -> screen timeout
    //                                          = PAS 2 -> support level
  
  }

  const sendProperties = () => {
    setRawData([])
    setBtBusy(true)
  
    let arr = [ 82 ]
    arr.push(propertyNumbers.length)
    arr = arr.concat(propertyNumbers)

  
    let checksum = crc16modbus(arr).toString(16)

    console.log("my checksum is", checksum)

    if (checksum.length < 4) {
      const difference = 4 - checksum.length
      
      for (let i=0; i < difference; i++) {
        checksum = "0" + checksum
      }

      console.log("my checksum is now", checksum)
    }

    arr.push("0x" + checksum[2] + checksum[3])
    arr.push("0x" + checksum[0] + checksum[1])
    arr.push(69)
    
    const writeValue = new Uint8Array(arr)

    btExecute(writeValue)
  
  }

  const changeSetting = (property, bytes, value) => {
      setRawData([])
      setBtBusy(true)

      const arr = [ 87 ]
      arr.push(2 + bytes)
      arr.push(property)
      arr.push(bytes)
  
      if (bytes === value.length) {
          const mergedArr = [
              ...arr,
              ...value
          ]
  
          const checksum = crc16modbus(mergedArr).toString(16)
          const checksumComplete = checksum.length === 2 ? "00" + checksum : checksum
          mergedArr.push("0x" + checksumComplete[2] + checksumComplete[3])
          mergedArr.push("0x" + checksumComplete[0] + checksumComplete[1])
          mergedArr.push(69)
        
          const writeValue = new Uint8Array(mergedArr)

          btExecute(writeValue)
      } else {
          console.log("number of bytes and value array length do not match – changes not approved")
      }
  }
  
  const btExecute = (action) => {
    service?.getCharacteristic("6e400002-b5a3-f393-e0a9-e50e24dcca9e")
    .then((characteristic) => {
      return characteristic
    })
    .then((characteristic) => {
      characteristic.writeValue(action)
      console.log(action, " written ")
      console.log(buf2hex(action), " written")
    })
    .then(() => {
      setBtBusy(false)
    })
    .catch((error) => {
      console.log("error sending propertyNumbers: ", error.message);
    }); 
  }

  useEffect(() => {
    if (service && ! polling) {
      console.log(`successfully connected, my service is ${service}`)
      interval.current = setInterval(() => {
        ! btBusy && sendProperties()
      }, 2000)
      setPolling(true)
    }
  }, [service])

  useEffect(() => {
    const payloadWithoutChecksum = rawData.slice(0, -3)
    const uint8Arr = new Uint8Array([rawData[rawData.length - 2], rawData[rawData.length - 3]])
    const checksum = buf2hex(uint8Arr.buffer)
    let checksumCalculated = crc16modbus(payloadWithoutChecksum).toString(16)
    checksumCalculated = checksumCalculated.length === 3 ? "0" + checksumCalculated : checksumCalculated

    // console.log("checksum is:", checksum)
    // console.log("checksumCalculated is:", checksumCalculated)

    if (rawData[0] === 82 && checksum === checksumCalculated) {
      let slicedRawData = rawData.slice(2, rawData.length - 3)
      let position = 0
      const dataObj = {}

      while (position < slicedRawData.length) {
        const property = slicedRawData[position]
        const bytes = slicedRawData[position + 1]
        const propertyName = properties.find(propertyObj => propertyObj.number === property)?.name || property

        switch(properties.find(propertyObj => propertyObj.number === property)?.type) {
          case "bool":
            dataObj[propertyName] = Boolean(slicedRawData[position + 2])
            break;
          case "bool-and-decimal":
            dataObj[propertyName] = {}
            dataObj[propertyName].bool = Boolean(slicedRawData[position + 2])
            dataObj[propertyName].value = parseInt(slicedRawData.slice(position + 3, position + 2 + bytes).map(int => int.toString(16)).join(""), 16)
            break;
          case "bool-and-hex":
            dataObj[propertyName] = {}
            dataObj[propertyName].bool = Boolean(slicedRawData[position + 2])
            dataObj[propertyName].value = buf2hex((new Uint8Array(slicedRawData.slice(position + 3, position + 2 + bytes))).buffer)
            break;
          case "decimal":
            dataObj[propertyName] = parseInt(slicedRawData.slice(position + 2, position + 2 + bytes).map(int => int.toString(16)).join(""), 16)   
            break;
          case "hex":      
            dataObj[propertyName] = buf2hex((new Uint8Array(slicedRawData.slice(position + 2, position + 2 + bytes))).buffer)
            break;
          case "utf8":      
            dataObj[propertyName] = new TextDecoder().decode(new Uint8Array(slicedRawData.slice(position + 2, position + 2 + bytes).filter(number => number > 0)))
            break;
          default:
            dataObj[propertyName] = {}
            dataObj[propertyName].hex = buf2hex((new Uint8Array(slicedRawData.slice(position + 2, position + 2 + bytes))).buffer)
            dataObj[propertyName].decimal = parseInt(slicedRawData.slice(position + 2, position + 2 + bytes).map(int => int.toString(16)).join(""), 16)
            dataObj[propertyName].utf8 = new TextDecoder().decode(new Uint8Array(slicedRawData.slice(position + 2, position + 2 + bytes)))
        }

        position = position + 2 + bytes
      }

      setData(dataObj)
      // console.log("raw data is: ", rawData)
    } else {
      console.log("raw data not set to data, its value is: ", rawData)
      console.log("raw data hex value is", buf2hex((new Uint8Array(rawData)).buffer))
    }
  }, [rawData])

  // console.log("this is my data: ", data)

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar 
          sx={{
            backgroundColor: defaultTheme.palette.secondary.dark
          }} 
          position="absolute"
        >
          <Container maxWidth="lg" sx={{px: "16px !important"}}>
            <Toolbar
              sx={{
                px: "24px !important"
              }}
            >
              <Typography
                component="h1"
                variant="h6"
                color="inherit"
                noWrap
                sx={{ flexGrow: 1 }}
              >
                Bike Monitor
              </Typography>
              <Stack direction="row" gap={2}>
                <Connect device={device} setDevice={setDevice} setRawData={setRawData} setService={setService} />
                <DebugDialog buf2hex={buf2hex} btSend={btSend} changeSetting={changeSetting} data={data} rawData={rawData} device={device} properties={properties} propertyNumbers={propertyNumbers} setPropertyNumbers={setPropertyNumbers} sendProperties={sendProperties} />
              </Stack>
            </Toolbar>
          </Container>
        </AppBar>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : '#141414',
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
              {/* Current Speed */}
              <Grid
                size={{
                  xxs: 12,
                  md: 5
                }}>
                <Paper
                  sx={{
                    p: {
                      xxs: 2,
                      lg: 3
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: "100%",
                    minHeight: 300,
                  }}
                >
                  <CurrentSpeed currentSpeed={device?.gatt?.connected ? Math.floor(data?.currentSpeed * multiplicator) : 0} unit={data?.unit ? "mi/h" : "km/h"} />
                </Paper>
              </Grid>
              <Grid
                container
                size={{
                  xxs: 12,
                  md: 7
                }}
                spacing={3}
                flexDirection="row"
                alignItems="top"
              >
                {/* Max. Speed */}
                <Grid
                  size={{
                    xxs: 12,
                    xs: 6
                  }}
                >
                  <Paper
                    sx={{
                      p: {
                        xxs: 2,
                        md: 3
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      height: "100%",
                    }}
                  >
                    <BigNumber icon={<SpeedIcon color="primary" fontSize="medium" />} property="Max. Speed" value={data.maxSpeed !== undefined ? Math.floor(data.maxSpeed * multiplicator) / 10 : "-"} unit={data?.unit ? "mi/h" : "km/h"} />
                  </Paper>
                </Grid>
                {/* Avg. Speed */}
                <Grid
                  size={{
                    xxs: 12,
                    xs: 6
                  }}
                >
                  <Paper
                    sx={{
                      p: {
                        xxs: 2,
                        md: 3
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      height: "100%",
                    }}
                  >
                    <BigNumber icon={<Typography color="primary" component="span" sx={{mt: "-5px", fontWeight: 200}} variant="h4">⌀</Typography>} property="Avg. Speed" value={data.avgSpeed !== undefined ? Math.floor(data.avgSpeed * multiplicator) / 10 : "-"} unit={data?.unit ? "mi/h" : "km/h"} />
                  </Paper>
                </Grid>
                {/* Trip */}
                <Grid
                  size={{
                    xxs: 12,
                    xs: 6
                  }}
                >
                  <Paper
                    sx={{
                      p: {
                        xxs: 2,
                        md: 3
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      height: "100%",
                    }}
                  >
                    <BigNumber icon={<DirectionsBikeIcon color="primary" fontSize="medium" />} property="Trip" value={data.trip !== undefined ? Math.floor(data.trip * multiplicator) / 10 : "-"} unit={data?.unit ? "mi" : "km"} />
                  </Paper>
                </Grid>
                {/* Trip time */}
                <Grid
                  size={{
                    xxs: 12,
                    xs: 6
                  }}
                >
                  <Paper
                    sx={{
                      p: {
                        xxs: 2,
                        md: 3
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      height: "100%",
                    }}
                  >
                    <BigNumber icon={<AccessTimeIcon color="primary" fontSize="medium" />} property="Trip time" value={data.tripTime !== undefined ? Math.floor(data.tripTime / 60) : "-"} unit="min" />
                  </Paper>
                </Grid>
                {/* Battery status */}
                <Grid
                  size={{
                    xxs: 12,
                    xs: 6,
                    lg: 4
                  }}
                >
                  <Paper
                    sx={{
                      p: {
                        xxs: 2,
                        md: 3
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      height: "100%",
                    }}
                  >
                    <BigNumber icon={<Battery5BarIcon color="primary" fontSize="medium" />} property="Battery" value={data.battery !== undefined ? data.battery : "-"} unit="%" />
                  </Paper>
                </Grid>
                {/* Range */}
                <Grid
                  size={{
                    xxs: 12,
                    xs: 6,
                    lg: 4
                  }}
                >
                  <Paper
                    sx={{
                      p: {
                        xxs: 2,
                        md: 3
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      height: "100%",
                    }}
                  >
                    <BigNumber icon={<ElectricBikeIcon color="primary" fontSize="medium" />} property="Range" value={data.range !== undefined && data.range !== 16776960 ? Math.floor(data.range / 10 * multiplicator, 1) : "-"} unit={data?.unit ? "mi" : "km"} />
                  </Paper>
                </Grid>
                {/* Total Mileage */}
                <Grid
                  size={{
                    xxs: 12,
                    lg: 4
                  }}
                >
                  <Paper
                    sx={{
                      p: {
                        xxs: 2,
                        md: 3
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      height: "100%",
                    }}
                  >
                    <BigNumber icon={<EmojiEventsIcon color="primary" fontSize="medium" />} property="Mileage" value={data.mileage !== undefined ? Math.floor(data?.mileage / 10 * multiplicator) : "-"} unit={data?.unit ? "mi" : "km"} />
                  </Paper>
                </Grid>
              </Grid>
              {/* Settings */}
              <Grid
                size={{
                  xxs: 12
                }}
              >
                <Paper 
                  sx={{ 
                    p: {
                      xxs: 2,
                      md: 3
                    },
                    display: 'flex', 
                    flexDirection: 'column' 
                  }}>
                  <Settings btBusy={btBusy} changeSetting={changeSetting} data={data} setData={setData} device={device} interval={interval} polling={polling} setPolling={setPolling} properties={properties} sendProperties={sendProperties} setRawData={setRawData} />
                </Paper>
              </Grid>
            </Grid>
            <Copyright sx={{ pt: 4 }} />
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}